<template>
  <v-dialog
    v-model="dialog"
    max-width="750px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        class="my-0"
        text
        icon
        color="primary"
        v-on="on"
      >
        <v-icon medium>
          add
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Promise"
      text="Apply for a promise"
      class="mb-0"
    >
      <form>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              class="pb-0"
            >
              <span> Promise Type </span>
              <v-radio-group
                v-model="is_private"
                row
                @change="loadData(is_private)"
              >
                <v-radio
                  class="mb-0"
                  label="Public"
                  :value="0"
                />
                <v-radio
                  class="mb-0"
                  label="Private"
                  :value="1"
                />
              </v-radio-group>
            </v-col>
            <v-col
              cols="12"
              class="mb-0 pb-0 pt-0"
            >
              <v-autocomplete
                v-model="superior"
                v-validate="'required'"
                :loading="loading"
                :items="employees"
                label="Select Superior"
                data-vv-name="promiser_uid"
                data-vv-as="Superior"
                hide-selected
                item-text="user.name"
                item-value="employee_uid"
                :error-messages="errors.collect('promiser_uid')"
              />
            </v-col>

            <v-col
              cols="12"
              lg="12"
              class="pb-0"
            >
              <v-textarea
                v-model="conditions"
                v-validate="'required'"
                data-vv-name="conditions"
                data-vv-as="Condition"
                :error-messages="errors.collect('conditions')"
                outlined
                name="input-7-4"
                label="Conditions"
                placeholder="Enter your conditions which you will be promising"
              />
            </v-col>

            <v-col
              class="mt-0 py-0"
              cols="12"
              lg="12"
            >
              <v-textarea
                v-model="promise"
                v-validate="'required'"
                data-vv-name="promise"
                data-vv-as="Promise"
                :error-messages="errors.collect('promise')"
                outlined
                name="input-7-4"
                label="What is Promised"
                placeholder="Enter what you will be promised"
              />
            </v-col>

            <v-col
              class="py-0"
              cols="12"
              lg="12"
            >
              <v-menu
                id="date"
                v-model="dueDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="dueDateDisplay"
                    v-validate="'required'"
                    label="Due date of the Promise"
                    prepend-icon="event"
                    readonly
                    data-vv-name="due_date"
                    data-vv-as="Due date"
                    :error-messages="errors.collect('due_date')"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="dueDate"                
                  class="datepicker"
                  no-title
                  @input="dueDateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12">
              <helper-file-input
                v-model="file" 
                :max-size-in-mb="2"
                :max-number="5"
                :multiple="true"
                data-vv-name="files"
                field-name="files"
                accept-type=".pdf"    
                extension="ext:pdf"      
              >
                <div 
                  slot="activator"
                  class="mb-2"
                >   
                  <v-btn                    
                    :disabled="progressing"
                    class="mx-2"    
                  >
                    Add file
                  </v-btn>
                  <span> 
                    {{ file ? '' : 'No file chosen (.pdf only)' }}
                  </span>
                  <div
                    v-if="file"
                    class="mt-4"
                  >
                    <p
                      v-for="selectedFile in file.selectedFiles"
                      :key="selectedFile.name"
                      class="mb-0"
                    >
                      <span class="font-weight-bold">{{ selectedFile.name }}</span>
                    </p>
                  </div>
                </div>
              </helper-file-input>
              <span class="red--text text--lighten-1">{{ errors.first('files') }}</span>
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Apply
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      HelperFileInput: () => import('@/components/helper/FileInput'),
    },
    inject: ['$validator'],
    props: {
      employeeUid: {
        type: String,
        default: '',
      },    
    },
    data () {
      return {
        loading: false,
        progressing: false,
        dialog: null,
        dueDateMenu: false,
        dueDate: null,
        superior: null,
        employees: [],
        conditions: null,
        promise: null,
        is_private: 0,
        file: null,
      }
    },
    computed: {
      dueDateDisplay () {
        if (!this.dueDate) return
        return this.$moment(this.dueDate, 'YYYY-MM-DD').format('D MMM YYYY')
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) {
          this.$validator.reset()          
          this.$resetData()
        } else {
          this.loadData()
        }
      },
    },
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.setProgressing(true)
    
            this.$api.promises().create(this.prepareFormData())
              .then((res) => {
                if (res.status === 200) {
                  this.$snack({ message: 'Added new Promise' })
                  this.$emit('added')
                  this.dialog = false
                }
              })
              .then(() => {
                this.setProgressing(false)
              })
          })
      },
      loadData (isPrivate = 0) {
        this.loading = true
        this.$api.superiors().list(this.employeeUid, isPrivate === 0 ? '?filter[has_permission]=update promises' 
          : '?filter[has_permission]=view private promises')
          .then((res) => {            
            this.employees = res.data
            this.superior = null
            this.loading = false
          })
      },
      prepareFormData () {
        if (this.file) {
          this.file.formData.set('promiser_uid', this.superior)
          this.file.formData.set('conditions', this.conditions)
          this.file.formData.set('promise', this.promise)
          this.file.formData.set('due_date', this.dueDate)
          this.file.formData.set('is_private', this.is_private)

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('promiser_uid', this.superior)
        formData.append('conditions', this.conditions)
        formData.append('promise', this.promise)
        formData.append('due_date', this.dueDate)
        formData.append('is_private', this.is_private)

        return formData
      },
      setProgressing (status) {
        this.progressing = status
      },      
    },
  }
</script>

<style lang='css' scoped>
</style>
